import React, { useState, useRef } from "react";
import axios from "axios";
import { Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ImageUploader — admin-only image upload widget.
 * Uploads to /api/admin/upload with the admin token from AdminContext.
 * On success, appends the returned public URL to the parent's URL list.
 */
export default function ImageUploader({ token, onUploaded, label = "Upload image" }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const pick = () => fileRef.current?.click();

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, form, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      const publicUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onUploaded(publicUrl);
      toast.success("Uploaded", { description: file.name });
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="inline-block">
      <input type="file" accept="image/*" ref={fileRef} onChange={handleChange} className="hidden" data-testid="uploader-input" />
      <button
        type="button"
        onClick={pick}
        disabled={uploading}
        className="inline-flex items-center gap-2 h-10 px-4 rounded-full border border-neutral-300 hover:bg-neutral-100 text-sm font-semibold disabled:opacity-50"
        data-testid="uploader-btn"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? "Uploading..." : label}
      </button>
    </div>
  );
}

/** Chip showing an image URL with remove button */
export function UrlChip({ url, onRemove }) {
  return (
    <div className="inline-flex items-center gap-2 bg-neutral-100 rounded-full pl-2 pr-1 py-1 text-xs">
      <img src={url} alt="" className="w-6 h-6 object-cover rounded-full bg-white" />
      <span className="font-mono truncate max-w-[180px]">{url.split("/").pop()}</span>
      <button type="button" onClick={onRemove} className="p-1 hover:bg-neutral-200 rounded-full">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
